import React, { useEffect, useState } from "react";
import {
  Container,
  Box,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Typography,
  Grid,
  Button,
  IconButton,
  TablePagination,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import { RemoveRedEyeOutlined } from "@mui/icons-material";
import {
  DEFAULT_LIMIT,
  DEFAULT_ROWS_PER_PAGE_OPTIONS,
} from "../utils/constants";
import { useNavigate, useParams } from "react-router-dom";
import {
  addPharmacyReturnByIdApi,
  getPharmacyReturnByIdApi,
  rejectPharmacyReturnByIdApi,
} from "../services/medicine-tracking.service";
import { toast } from "react-toastify";
import Loader from "../components/Loader/loader";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const PharmacyMedicineReturn = () => {
  const { trackingId } = useParams();
  const navigate = useNavigate();
  const [medReqTrackList, setMedReqTrackList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allData, setAllData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const fetchData = async () => {
    if (!trackingId) {
      console.error("No trackingId provided");
      setIsError(true);
      setIsLoading(false);
      return;
    }

    try {
      const response = await getPharmacyReturnByIdApi({ trackingId });
      

      if (
        response?.isSuccess ||
        response?.success ||
        response?.code === 200
      ) {
        setAllData(response.data?.data);
        setIsError(false);
      } else {
        console.error("API returned error:", response);
        setIsError(true);
        toast.error(response?.message || "Failed to fetch pharmacy return data");
      }
    } catch (error) {
      console.error("Error fetching pharmacy return data:", error);
      setIsError(true);
      toast.error(error?.message || "Failed to fetch pharmacy return data");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [trackingId]);

  const patientInfo = allData?.patientInfo || {};
  
  const [selectedRows, setSelectedRows] = useState([]);

  // select all
  const handleSelectAllClick = (event) => {
    const checked = event.target.checked;
    const updatedList = medReqTrackList?.map((row) => ({
      ...row,
      isSelected: checked,
    }));
    setMedReqTrackList(updatedList);
    setSelectedRows(checked ? updatedList : []);
  };

  // const handleClick = (_id) => {
  //   const selectedIndex = selectedRows?.indexOf(_id);
  //   let newSelected = [];
  //   if (selectedIndex === -1) {
  //     newSelected = newSelected?.concat(selectedRows, _id);
  //   } else if (selectedIndex === 0) {
  //     newSelected = newSelected?.concat(selectedRows?.slice(1));
  //   } else if (selectedIndex === selectedRows?.length - 1) {
  //     newSelected = newSelected?.concat(selectedRows?.slice(0, -1));
  //   } else if (selectedIndex > 0) {
  //     newSelected = newSelected?.concat(
  //       selectedRows?.slice(0, selectedIndex),
  //       selectedRows?.slice(selectedIndex + 1)
  //     );
  //   }
  //   setSelectedRows(newSelected);
  // };

  const handleClick = (event, row) => {
    const updatedList = medReqTrackList?.map((r) =>
      r?.sNo === row?.sNo ? { ...r, isSelected: !r?.isSelected } : r
    );
    setMedReqTrackList(updatedList);
    setSelectedRows(updatedList?.filter((r) => r?.isSelected));
  };
  const handleReturnQuantityChange = (
    sNo,
    value,
    issuedQuantity,
    usedQuantity
  ) => {
    const updatedList = medReqTrackList?.map((row) =>
      row?.sNo === sNo
        ? {
            ...row,
            returnQuantity: Math.min(
              Math.max(0, parseInt(value) || 0),
              issuedQuantity - usedQuantity
            ),
          }
        : row
    );
    setMedReqTrackList(updatedList);
  };

  const handleAcceptReturn = async () => {
    if (selectedRows?.length === 0) {
      toast.error("Please select at least one medicine to return.");
      return;
    }

    setLoading(true);
    try {
      
      const response = await addPharmacyReturnByIdApi(trackingId, {
        medicines: selectedRows?.map((row) => ({
          medicineId: row?.medicineId, // Use medicineId directly from the response
          returnQuantity: row?.returnQuantity || 0,
        })),
      });

      if (
        response?.isSuccess ||
        response?.success ||
        response?.code === 200
      ) {
        toast.success("Medicine return accepted successfully!");
        // Navigate back to pharmacy page after successful acceptance
        setTimeout(() => {
          navigate('/pharmacy');
        }, 1500);
      } else {
        toast.error(response?.message || "Some error occurred.");
      }
    } catch (error) {
      console.error("Error accepting medicine return:", error);
      toast.error(error?.message || "Some error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const handleRejectReturn = async () => {
    setLoading(true);
    try {
      
      const response = await rejectPharmacyReturnByIdApi(trackingId, {
        reason: "Return request rejected by pharmacy",
      });


      if (response?.success || response?.code === 200) {
        toast.success("Medicine return rejected successfully!");
        // Navigate back to pharmacy page after successful rejection
        setTimeout(() => {
          navigate('/pharmacy');
        }, 1500);
      } else {
        toast.error(response?.message || "Some error occurred.");
      }
    } catch (error) {
      console.error("Error rejecting medicine return:", error);
      toast.error(error?.message || "Some error occurred.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setMedReqTrackList(
      allData?.pharmacyReturns?.map((option) => ({
        ...option,
        returnQuantity: option.returnedQuantity || 0, // Map returnedQuantity to returnQuantity
        isSelected: false,
      })) || []
    );
  }, [allData]);

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ width: "100%" }}>
          <Box
            sx={{
              padding: 2,
              marginTop: 2,
              borderRadius: 2,
              marginRight: "90px",
            }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{
                color: "#5D6A82",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: "bold",
                padding: 0,
                transition: "none !important",
                marginBottom: "16px",
              }}
            >
              Back
            </Button>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 0,
                mb: "20px",
                color: "#5D6A82",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Open Sans",
                  fontWeight: 600,
                  fontSize: "20px",
                  lineHeight: "29px",
                  letterSpacing: "0px",
                  color: "inherit",
                }}
              >
                Medicine Return
              </Typography>
            </Box>

          {isLoading ? (
            <Loader />
          ) : isError ? (
            <ErrorMessage />
          ) : (
            <>
              {/* Patient Information */}
              <Box
                sx={{
                  border: `1px solid #CCD4DA`,
                  borderRadius: "12px",
                  p: "20px",
                  mb: "30px",
                }}
              >
                <Typography sx={{ color: "#5D6A82", mb: 2, fontSize: "14px" }}>
                  Patient Information
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <TextField
                      label="UHID"
                      value={patientInfo?.uhid || ""}
                      fullWidth
                      required
                      slotProps={{ input: { readOnly: true } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <TextField
                      label="Patient Name"
                      value={patientInfo?.patientName || ""}
                      fullWidth
                      required
                      slotProps={{ input: { readOnly: true } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <TextField
                      label="Ward Name"
                      fullWidth
                      value={patientInfo?.wardName || ""}
                      required
                      slotProps={{ input: { readOnly: true } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <TextField
                      label="Bed Number"
                      fullWidth
                      value={patientInfo?.bedNumber || ""}
                      required
                      slotProps={{ input: { readOnly: true } }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Invoice Number */}
              <Typography sx={{ mb: "30px", fontWeight: 600 }}>
                Invoice Number : {allData?.invoiceNumber || "N/A"}
              </Typography>

              {/* Table */}

              <TableContainer component={Paper}>
                <Table aria-label="patient table">
                  <TableHead
                    sx={{
                      backgroundColor: "#f5f5f5",
                      "& th": {},
                    }}
                  >
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={
                            selectedRows?.length > 0 &&
                            selectedRows?.length < medReqTrackList?.length
                          }
                          checked={
                            medReqTrackList?.length > 0 &&
                            selectedRows?.length === medReqTrackList?.length
                          }
                          onChange={handleSelectAllClick}
                        />
                      </TableCell>
                      <TableCell>SNo</TableCell>
                      <TableCell>Item Code</TableCell>
                      <TableCell>Medicine Name</TableCell>

                      <TableCell>Issued Quantity</TableCell>
                      <TableCell>Used Quantity</TableCell>
                      <TableCell>Returned Quantity</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {medReqTrackList?.map((row, index) => {
                      return (
                        <TableRow
                          key={row?.sNo}
                          tabIndex={-1}
                          role="checkbox"
                          selected={row?.isSelected}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              disableRipple
                              checked={row?.isSelected}
                              onChange={(event) => handleClick(event, row)}
                              disabled={row?.returnStatus === "Returned"}
                            />
                          </TableCell>
                          <TableCell>{row?.sNo || "-"}</TableCell>
                          <TableCell>{row?.itemCode || "-"}</TableCell>
                          <TableCell>{row?.itemName || "-"}</TableCell>
                          <TableCell>{row?.issuedQuantity || "0"}</TableCell>
                          <TableCell>{row?.usedQuantity || "0"}</TableCell>
                                                     <TableCell>
                             <TextField
                               type="number"
                               value={row?.returnQuantity || ""}
                               onChange={(e) =>
                                 handleReturnQuantityChange(
                                   row?.sNo,
                                   e.target.value,
                                   row?.issuedQuantity,
                                   row?.usedQuantity
                                 )
                               }
                               disabled={
                                 !row?.isSelected ||
                                 row?.returnStatus === "Returned"
                               }
                             />
                           </TableCell>

                          <TableCell align="right">
                            {/* <IconButton onClick={() => handleOpenModal(row)}>
                            <RemoveRedEyeIcon sx={{ fontSize: "18px" }} />
                          </IconButton> */}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
                                           {/* Action buttons */}
            <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: "20px" }}>
              <Button
                variant="contained"
                color="primary"
                sx={{
                  minWidth: "150px",
                  textTransform: "none",
                }}
                onClick={handleAcceptReturn}
                disabled={loading || selectedRows?.length === 0}
              >
                {loading ? <CircularProgress size={24} /> : `Accept Return`}
              </Button>
              <Button
                variant="outlined"
                color="error"
                sx={{
                  minWidth: "150px",
                  textTransform: "none",
                }}
                onClick={handleRejectReturn}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : `Reject Return`}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default PharmacyMedicineReturn;
